import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, FolderOpen, Loader2, Building2, CheckCircle2, AlertTriangle, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import type { Project } from "@/api/types";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal, Field, inputCls } from "@/components/Modal";

const PROPERTY_TYPES = ["apartment", "house", "studio", "office", "commercial"];

export default function Dashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", property_type: "apartment", city: "", country: "France", label: "" });

  const { data: projects = [], isLoading } = useQuery<Project[]>({ queryKey: ["projects"], queryFn: api.listProjects });

  const createMut = useMutation({
    mutationFn: () => api.createProject({
      name: form.name, description: form.description, property_type: form.property_type,
      address: { label: form.label, city: form.city, country: form.country },
    } as any),
    onSuccess: (p) => {
      toast.success("Projet créé");
      qc.invalidateQueries({ queryKey: ["projects"] });
      setOpen(false);
      navigate(`/projects/${p._id}`);
    },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const metrics = useMemo(() => ({
    total: projects.length,
    validated: projects.filter((p) => p.status === "validated" || p.status === "exported").length,
    review: projects.filter((p) => p.status === "needs_review").length,
    exported: projects.filter((p) => p.status === "exported").length,
  }), [projects]);

  const visible = projects.filter((p) =>
    (filter === "all" || p.status === filter) &&
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const cards = [
    { label: "Projets", value: metrics.total, icon: Building2, color: "text-primary" },
    { label: "Validés", value: metrics.validated, icon: CheckCircle2, color: "text-emerald-400" },
    { label: "À revoir", value: metrics.review, icon: AlertTriangle, color: "text-amber-400" },
    { label: "Exportés", value: metrics.exported, icon: PackageCheck, color: "text-cyan-400" },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Tableau de bord</h1>
          <p className="mt-1 text-sm text-muted-foreground">Vos projets de scan de logement, revue et validation.</p>
        </div>
        <button
          data-testid="create-project-button"
          onClick={() => setOpen(true)}
          className="flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> Nouveau projet
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-border/60 bg-card p-5">
            <c.icon className={`h-5 w-5 ${c.color}`} />
            <div className="mt-3 font-mono text-3xl font-bold">{c.value}</div>
            <div className="uppercase-label mt-1 text-muted-foreground">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="dashboard-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un projet…"
            className={`${inputCls} pl-9`}
          />
        </div>
        <select
          data-testid="dashboard-filter-status"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className={`${inputCls} sm:w-56`}
        >
          <option value="all">Tous les statuts</option>
          {["draft", "scanning", "processing", "needs_review", "validated", "exported", "archived"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-20 text-center">
          <FolderOpen className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-3 text-muted-foreground">Aucun projet. Créez votre premier projet de scan.</p>
        </div>
      ) : (
        <div data-testid="dashboard-project-grid" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((p) => (
            <button
              key={p._id}
              data-testid={`project-card-${p._id}`}
              onClick={() => navigate(`/projects/${p._id}`)}
              className="group rounded-xl border border-border/60 bg-card p-5 text-left transition-all hover:border-primary/40 hover:bg-[#1A2234]"
            >
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 border border-primary/20">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <StatusBadge status={p.status} />
              </div>
              <h3 className="mt-4 text-lg font-semibold group-hover:text-primary">{p.name}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{p.description || "—"}</p>
              <div className="mt-4 flex items-center gap-2 uppercase-label text-muted-foreground">
                <span>{p.property_type}</span>
                {p.address?.city && <span>· {p.address.city}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Nouveau projet" testId="create-project-modal">
        <form
          onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }}
          className="space-y-4"
        >
          <Field label="Nom du projet">
            <input data-testid="project-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Appartement Lyon" />
          </Field>
          <Field label="Description">
            <textarea data-testid="project-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} rows={2} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type de bien">
              <select data-testid="project-type-input" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className={inputCls}>
                {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Ville">
              <input data-testid="project-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} placeholder="Lyon" />
            </Field>
          </div>
          <Field label="Adresse">
            <input data-testid="project-address-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className={inputCls} placeholder="10 rue Exemple" />
          </Field>
          <button
            data-testid="project-submit-button"
            type="submit"
            disabled={createMut.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {createMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Créer le projet
          </button>
        </form>
      </Modal>
    </div>
  );
}
